import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { alreadyInstalled, promptInstall, queryInstalled, subscribeInstallState } from './pwaInstall';
import './MobileLayout.css';

const GUIDES = {
    android: {
        title: 'Instalar en Android',
        intro: 'No se descarga un archivo. Se instala desde Chrome y queda un ícono para abrirla como una aplicación.',
        steps: [
            'Abrí Marketplace en Chrome, con la misma dirección que usás en la computadora.',
            'Tocá el menú de los tres puntos, arriba a la derecha.',
            'Elegí “Instalar app” o “Agregar a la pantalla principal”.',
            'Confirmá. El ícono queda en el inicio y Marketplace se abre en su propia ventana.',
        ],
    },
    ios: {
        title: 'Instalar en iPhone o iPad',
        intro: 'En el iPhone no hay un botón de instalación automático. Se agrega desde Safari.',
        steps: [
            'Abrí Marketplace en Safari. Tiene que ser Safari: Chrome en el iPhone no permite instalarla.',
            'Tocá el botón Compartir, el cuadrado con la flecha hacia arriba.',
            'Deslizá la lista y elegí “Agregar a inicio”.',
            'Confirmá con Agregar. El ícono queda en la pantalla de inicio.',
            'Abrila desde ese ícono. Ahí se puede permitir las notificaciones del sistema, en iOS 16.4 o posterior.',
        ],
    },
};

export default function DownloadApps({ compact = false }) {
    const [open, setOpen] = useState(false);
    const [platform, setPlatform] = useState(null);
    const [installed, setInstalled] = useState(alreadyInstalled);

    useEffect(() => {
        let cancelled = false;
        const apply = (installedNow) => {
            if (!cancelled) setInstalled(installedNow);
        };
        const unsubscribe = subscribeInstallState(apply);
        const check = () => {
            queryInstalled().then(apply);
        };
        check();
        const onVisible = () => {
            if (document.visibilityState === 'visible') check();
        };
        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener('focus', check);
        return () => {
            cancelled = true;
            unsubscribe();
            document.removeEventListener('visibilitychange', onVisible);
            window.removeEventListener('focus', check);
        };
    }, []);

    useEffect(() => {
        if (!open) return undefined;
        const onKey = (event) => {
            if (event.key === 'Escape') {
                setOpen(false);
                setPlatform(null);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open]);

    const close = () => {
        setOpen(false);
        setPlatform(null);
    };

    const guide = platform ? GUIDES[platform] : null;

    const dialog = open ? (
        <div className="download-apps-backdrop" onClick={close} role="presentation">
            <div
                className="download-apps-dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="download-apps-title"
                onClick={(event) => event.stopPropagation()}
            >
                {platform === 'pc' ? (
                    <>
                        <h2 id="download-apps-title" className="download-apps-dialog__title">Instalar en esta computadora</h2>
                        <p className="download-apps-dialog__intro">
                            Este navegador no mostró el instalador automático. En Chrome o Edge igual se puede instalar a mano.
                        </p>
                        <ol className="download-apps-dialog__steps">
                            <li>Abrí el menú del navegador, los tres puntos arriba a la derecha.</li>
                            <li>Elegí “Instalar Marketplace” o “Aplicaciones” y después instalar el sitio.</li>
                            <li>También puede aparecer un ícono de instalación en la barra de direcciones.</li>
                            <li>Confirmá. Marketplace se abre en su propia ventana, como un programa.</li>
                        </ol>
                        <div className="download-apps-dialog__actions">
                            <button type="button" className="download-apps-dialog__back" onClick={() => setPlatform(null)}>
                                Volver
                            </button>
                            <button type="button" className="download-apps-dialog__close" onClick={close}>
                                Cerrar
                            </button>
                        </div>
                    </>
                ) : guide ? (
                    <>
                        <h2 id="download-apps-title" className="download-apps-dialog__title">{guide.title}</h2>
                        <p className="download-apps-dialog__intro">{guide.intro}</p>
                        <ol className="download-apps-dialog__steps">
                            {guide.steps.map((step) => (
                                <li key={step}>{step}</li>
                            ))}
                        </ol>
                        <div className="download-apps-dialog__actions">
                            <button type="button" className="download-apps-dialog__back" onClick={() => setPlatform(null)}>
                                Volver
                            </button>
                            <button type="button" className="download-apps-dialog__close" onClick={close}>
                                Cerrar
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <h2 id="download-apps-title" className="download-apps-dialog__title">Instalar la aplicación</h2>
                        <p className="download-apps-dialog__intro">
                            Elegí el celular. Al instalar se abre la guía para dejarla en ese equipo.
                        </p>
                        <div className="download-apps-dialog__choices">
                            <button type="button" onClick={() => setPlatform('android')}>
                                <strong>Android</strong>
                                <span>Instalar</span>
                            </button>
                            <button type="button" onClick={() => setPlatform('ios')}>
                                <strong>iOS</strong>
                                <span>Instalar</span>
                            </button>
                        </div>
                        <div className="download-apps-dialog__pc">
                            <p>
                                En la computadora, Chrome o Edge pueden instalarla en este equipo y abrirla en su propia ventana.
                            </p>
                            <button
                                type="button"
                                className="download-apps-dialog__install"
                                onClick={async () => {
                                    const choice = await promptInstall();
                                    if (!choice) setPlatform('pc');
                                    else if (choice.outcome === 'accepted') close();
                                }}
                            >
                                Instalar en esta computadora
                            </button>
                        </div>
                        <div className="download-apps-dialog__actions">
                            <button type="button" className="download-apps-dialog__close" onClick={close}>
                                Cerrar
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    ) : null;

    if (installed) return null;

    return (
        <>
            <button
                type="button"
                className={`download-apps${compact ? ' download-apps--compact' : ''}`}
                onClick={() => setOpen(true)}
                aria-haspopup="dialog"
                aria-expanded={open}
            >
                Instalar
            </button>
            {dialog && createPortal(dialog, document.body)}
        </>
    );
}
